import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, List, ListItem, ListItemText, Snackbar, SnackbarCloseReason, TextField, Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useDebounce } from '../../../shared/hooks';
import { ContaBancariaService } from '../../../shared/services/api/cadastros/ContaBancaria';
import { BoletosReceberService, IListagemBoletoReceber } from '../../../shared/services/api/Financeiro/BoletosReceber';
import { formatarMoeda } from '../../../shared/utils/formatarMoeda';

export interface DialogCompensarBoletosProps {
  open: boolean;
  boletos: IListagemBoletoReceber[];
  onClose: (value?: string) => void;
}

type TContaOpcao = { idconta: string; label: string };

// Compensação real de um ou mais boletos selecionados na listagem de
// Recebíveis - Boletos (ver Conciliacao.tsx): o valor bruto já vem somado dos
// selecionados, o operador só confirma quanto o banco efetivamente depositou
// (pode vir menor por tarifa bancária) — a diferença é rateada entre os
// boletos proporcionalmente (ver BoletosReceber/Compensar.ts, backend).
export const DialogCompensarBoletos: React.FC<DialogCompensarBoletosProps> = ({ open, boletos, onClose }) => {
  const { debounce } = useDebounce();

  const [busca, setBusca] = useState('');
  const [opcoesConta, setOpcoesConta] = useState<TContaOpcao[]>([]);
  const [carregandoContas, setCarregandoContas] = useState(false);
  const [contaDestino, setContaDestino] = useState<TContaOpcao | null>(null);

  const [data, setData] = useState(dayjs().format('YYYY-MM-DD'));
  const [valorLiquidoTexto, setValorLiquidoTexto] = useState('');

  const [compensando, setCompensando] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const valorBruto = useMemo(() => boletos.reduce((soma, b) => soma + Number(b.valorbruto || 0), 0), [boletos]);

  useEffect(() => {
    if (!open) return;
    setBusca('');
    setContaDestino(null);
    setData(dayjs().format('YYYY-MM-DD'));
    setValorLiquidoTexto(String(Math.round(valorBruto * 100)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setCarregandoContas(true);
    debounce(() => {
      ContaBancariaService.getAllList(busca, '', 'banco', 20).then((resultado) => {
        setCarregandoContas(false);
        if (resultado instanceof Error) { setOpcoesConta([]); return; }
        setOpcoesConta(resultado.data.map((conta) => ({ idconta: conta.idconta, label: conta.titular })));
      });
    });
  }, [open, busca, debounce]);

  const valorLiquido = valorLiquidoTexto ? Number(valorLiquidoTexto) / 100 : 0;
  const taxa = useMemo(() => Math.max(0, Number((valorBruto - valorLiquido).toFixed(2))), [valorBruto, valorLiquido]);

  const exibir = (valor: number) => (valor === 0 ? '' : formatarMoeda(valor));
  const handleChangeValorLiquido = (texto: string) => setValorLiquidoTexto(texto.replace(/\D/g, ''));

  const handleCloseMsg = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
    if (tms === 'success') onClose('true');
  };

  const confirmar = () => {
    if (boletos.length === 0) return;
    if (!contaDestino) {
      setTms('warning');
      setMsg('Selecione a conta bancária de destino');
      setOpenmsg(true);
      return;
    }
    if (valorLiquido <= 0) {
      setTms('warning');
      setMsg('Informe o valor que o banco depositou');
      setOpenmsg(true);
      return;
    }
    if (valorLiquido > valorBruto + 0.01) {
      setTms('warning');
      setMsg('O valor depositado não pode ser maior que o valor bruto dos boletos selecionados');
      setOpenmsg(true);
      return;
    }

    setCompensando(true);
    BoletosReceberService
      .compensar({
        ids: boletos.map((b) => b.idboletoreceber),
        idcontadestino: contaDestino.idconta,
        valorliquido: valorLiquido,
        datalan: dayjs(data, 'YYYY-MM-DD').toDate(),
      })
      .then((resultado) => {
        setCompensando(false);
        if (resultado instanceof Error) {
          setTms('error');
          setMsg(resultado.message);
          setOpenmsg(true);
          return;
        }
        setTms('success');
        setMsg('Boletos compensados com sucesso!');
        setOpenmsg(true);
      });
  };

  const handleCancel = () => {
    if (compensando) return;
    onClose('false');
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Compensar boleto{boletos.length > 1 ? 's' : ''}</DialogTitle>
      <DialogContent dividers>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 2500} onClose={handleCloseMsg}>
          <Alert onClose={handleCloseMsg} severity={tms} variant="filled" sx={{ width: '100%' }}>
            {msg}
          </Alert>
        </Snackbar>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {boletos.length} boleto{boletos.length !== 1 ? 's' : ''} selecionado{boletos.length !== 1 ? 's' : ''}
        </Typography>
        <List dense disablePadding sx={{ mb: 2, maxHeight: 160, overflowY: 'auto', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1 }}>
          {boletos.map((b) => (
            <ListItem key={b.idboletoreceber} divider>
              <ListItemText
                primary={b.parceironome}
                secondary={`CR: ${b.numerocr}`}
              />
              <Typography variant="body2" fontWeight={600}>
                {formatarMoeda(Number(b.valorbruto))}
              </Typography>
            </ListItem>
          ))}
        </List>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Conta de destino (banco)
        </Typography>
        <Autocomplete
          openText='Abrir'
          closeText='Fechar'
          noOptionsText='Nenhuma conta encontrada'
          loadingText='Carregando...'
          disablePortal
          options={opcoesConta}
          loading={carregandoContas}
          disabled={compensando}
          value={contaDestino}
          onInputChange={(_, newValue) => setBusca(newValue)}
          onChange={(_, newValue) => setContaDestino(newValue)}
          popupIcon={carregandoContas ? <CircularProgress size={20} /> : undefined}
          renderInput={(params) => (
            <TextField {...params} size='small' label='Conta de destino' />
          )}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Valor bruto dos boletos"
            value={exibir(valorBruto)}
            InputProps={{ readOnly: true }}
            disabled
          />
          <TextField
            size="small"
            type="date"
            label="Data da compensação"
            InputLabelProps={{ shrink: true }}
            value={data}
            onChange={(e) => setData(e.target.value)}
            disabled={compensando}
            sx={{ minWidth: 170 }}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <TextField
          fullWidth
          size="small"
          label="Valor que o banco depositou"
          placeholder="0,00"
          value={exibir(valorLiquido)}
          onChange={(e) => handleChangeValorLiquido(e.target.value)}
          inputProps={{ inputMode: 'numeric' }}
          disabled={compensando}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">Tarifa bancária</Typography>
          <Typography variant="body1" fontWeight={700} color={taxa > 0 ? 'error.main' : 'text.primary'}>
            R$ {formatarMoeda(taxa)}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={handleCancel} disabled={compensando}>Cancelar</Button>
        <Button variant="outlined" autoFocus disabled={compensando} onClick={confirmar}>
          {compensando ? 'Compensando...' : 'Confirmar compensação'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
